import React, { useState, useEffect } from 'react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { X, Shield, BarChart3, Megaphone, Heart } from 'lucide-react';

const CATEGORIES = [
  {
    key: 'essential',
    label: 'Essential',
    icon: Shield,
    description: 'Required for the website to function — session, security, and cart.',
    locked: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    description: 'Anonymous usage data to help us improve the shopping experience.',
  },
  {
    key: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    description: 'Used to deliver relevant ads and measure campaign effectiveness.',
  },
  {
    key: 'preferences',
    label: 'Preferences',
    icon: Heart,
    description: 'Remember your wishlist, recently viewed, and display settings.',
  },
];

export const CookieSettingsModal = () => {
  const { showSettings, closeSettings, consent, saveConsent, acceptAll } = useCookieConsent();
  const [prefs, setPrefs] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    if (consent) {
      setPrefs({
        essential: true,
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        preferences: !!consent.preferences,
      });
    }
  }, [consent, showSettings]);

  if (!showSettings) return null;

  const toggle = (key) => {
    if (key === 'essential') return;
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" data-testid="cookie-settings-modal">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[3px] animate-fadeIn"
        onClick={closeSettings}
      />

      {/* Modal */}
      <div className="relative bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[80vh] sm:max-h-[85vh] overflow-hidden animate-scaleIn sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E2E2]">
          <h2 className="text-base font-heading font-bold text-charcoal">Cookie Preferences</h2>
          <button
            onClick={closeSettings}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F7F5F2] transition-colors"
            data-testid="cookie-settings-close-btn"
          >
            <X className="w-4 h-4 text-charcoal" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-2.5 overflow-y-auto max-h-[50vh]">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.key}
                className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E2E2] hover:border-[#C9A84C]/25 transition-colors"
                data-testid={`cookie-category-${cat.key}`}
              >
                <div className="w-8 h-8 rounded-md bg-[#F7F5F2] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#C9A84C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h3 className="text-[13px] font-bold text-charcoal leading-tight">{cat.label}</h3>
                      {cat.locked && (
                        <span className="text-[9px] uppercase tracking-wider font-bold text-[#C9A84C]">Always Active</span>
                      )}
                    </div>
                    <button
                      onClick={() => toggle(cat.key)}
                      disabled={cat.locked}
                      className={`relative w-10 h-[22px] rounded-full transition-colors duration-200 flex-shrink-0 ${
                        prefs[cat.key] ? 'bg-[#C9A84C]' : 'bg-[#E2E2E2]'
                      } ${cat.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`cookie-toggle-${cat.key}`}
                      aria-label={`Toggle ${cat.label}`}
                    >
                      <span
                        className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-200 ${
                          prefs[cat.key] ? 'translate-x-[18px]' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-[11px] text-softgrey leading-snug mt-0.5">{cat.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#E2E2E2] flex gap-2.5">
          <button
            onClick={() => saveConsent(prefs)}
            className="flex-1 bg-charcoal hover:bg-black text-white font-bold text-[10px] uppercase tracking-widest h-10 rounded-md transition-all duration-200"
            data-testid="cookie-save-preferences-btn"
          >
            Save Preferences
          </button>
          <button
            onClick={acceptAll}
            className="flex-1 bg-[#C9A84C] hover:bg-[#D4B04A] text-black font-bold text-[10px] uppercase tracking-widest h-10 rounded-md transition-all duration-200"
            data-testid="cookie-accept-all-modal-btn"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};
